import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';

import { ComponentAbstract } from '@app/components/abstract/component.abstract';
import { EntityTypeListConst } from '@shared/constants/entity-type-list.const';
import { WorkflowStepListConst } from '@shared/constants/workflow-step-list.const';
import { EntityType } from '@shared/enums/entity-type';
import { EIcon } from '@shared/enums/icon.enum';
import { NotificationCategory } from '@shared/enums/notification-category';
import { UserType } from '@shared/enums/user-type';
import { WorkflowStepEnumType } from '@shared/enums/workflow-step.enum';
import { WebNotification } from '@shared/interfaces/notification.interface';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { EnumerationValuePipe } from '@shared/pipes/enumeration-value.pipe';

@Component({
  selector: 'app-notification-ring-item',
  templateUrl: './notification-ring-item.component.html',
  styleUrls: ['./notification-ring-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationRingItemComponent extends ComponentAbstract implements OnInit {
  entityList: IRadioButtonOption<EntityType>[] = EntityTypeListConst;

  title = '';
  content = '';
  footer = '';

  icon: EIcon;
  deleteInProgress = false;

  @Input() isOdd = false;
  @Input() notification: WebNotification;
  @Input() deleteDisabled: boolean;
  @Output() selectEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() deleteEvent: EventEmitter<void> = new EventEmitter<void>();

  constructor(protected cdr: ChangeDetectorRef, private enumerationValuePipe: EnumerationValuePipe) {
    super(cdr);
  }

  ngOnInit(): void {
    this.icon = this.getNotificationIcon();
    this.title = this.getNotificationTitle();
    this.content = this.getNotificationContent();
    this.footer = this.getNotificationFooter();
  }

  select() {
    this.selectEvent.emit();
  }

  delete($event: MouseEvent) {
    this.deleteInProgress = true;
    this.cdr.detectChanges();
    $event.stopPropagation();
    this.deleteEvent.emit();
  }

  private getNotificationIcon(): EIcon {
    switch (this.notification.category) {
      case NotificationCategory.TurnoverComment:
        return EIcon.COMMENT2;
      case NotificationCategory.Bid:
        return EIcon.DOLLAR;
      case NotificationCategory.BidAccepted:
        return EIcon.DOLLAR;
      case NotificationCategory.BidRejected:
        return EIcon.DOLLAR;
      case NotificationCategory.BidComment:
        return EIcon.COMMENT2;
      case NotificationCategory.Ticket:
        return EIcon.TICKET;
      case NotificationCategory.BidSentBack:
        return EIcon.DOLLAR;
      case NotificationCategory.Turnover:
        return EIcon.TIME_SCHEDULE;
      case NotificationCategory.TurnStepSchedule:
        return EIcon.TIME_SCHEDULE;
    }
  }

  private getNotificationTitle(): string {
    if (this.notification.category === NotificationCategory.TurnStepSchedule) {
      return this.notification.title;
    }
    switch (this.notification.relatedObjectTypeId) {
      case EntityType.WorkflowStep: {
        return `${
          this.enumerationValuePipe.transform(
            this.notification.additionalData?.workflowStepEnumType,
            WorkflowStepListConst
          ) || ''
        }`;
      }
      case EntityType.Ticket: {
        return this.notification.additionalData?.ticketName || 'Ticket';
      }
      case EntityType.Bid: {
        return `${this.enumerationValuePipe.transform(WorkflowStepEnumType.Bid, WorkflowStepListConst) || ''}`;
      }
      default: {
        return `${this.enumerationValuePipe.transform(this.notification.relatedObjectTypeId, this.entityList) || ''}`;
      }
    }
  }

  private getNotificationFooter() {
    const unitName = this.notification.additionalData?.unitName
      ? `- ${this.notification.additionalData?.unitName}`
      : '';
    const unitNames = this.notification.additionalData?.unitNames
      ? `- ${this.notification.additionalData?.unitNames.join(', ')}`
      : '';
    return `${this.notification.additionalData?.propertyName || ''} ${unitName || unitNames}`;
  }

  private getNotificationContent() {
    switch (this.notification.category) {
      case NotificationCategory.Turnover: {
        return 'A turnover has been assigned to you';
      }
      case NotificationCategory.TurnoverComment: {
        return 'New comment';
      }
      case NotificationCategory.Bid: {
        return 'A new bid is available';
      }
      case NotificationCategory.BidAccepted: {
        return 'Bid accepted';
      }
      case NotificationCategory.BidRejected: {
        return 'Bid rejected';
      }
      case NotificationCategory.BidComment: {
        return this.notification.additionalData?.userType === UserType.Contractor
          ? `A new comment from ${this.notification.additionalData?.companyName} was added.`
          : 'A new comment was added';
      }
      case NotificationCategory.Ticket: {
        return 'A ticket has been assigned to you.';
      }
      case NotificationCategory.BidSentBack: {
        return 'A new offer is available.';
      }
      default: {
        return this.notification?.content || '';
      }
    }
  }
}
